import { useState } from 'react';
import { Plus, ChevronDown, Sparkles, Tag, Target, Calendar, Clock, Zap, DollarSign, Users, Settings } from 'lucide-react';

interface UserRestrictions {
  type: 'all' | 'new_users' | 'inactive_users' | 'verified_only' | 'custom';
  newUserDays?: number;
  inactiveDays?: number;
  minProjects?: number;
  maxProjects?: number;
  emailVerified?: boolean;
  neverPurchased?: boolean;
}

interface PromoCodeFormProps {
  onSubmit: (e: React.FormEvent) => void;
  presets: any;
  onPresetChange: (preset: string) => void;
  // Basic
  code: string;
  setCode: (v: string) => void;
  credits: string;
  setCredits: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  // Advanced
  showAdvanced: boolean;
  setShowAdvanced: (v: boolean) => void;
  selectedPreset: string;
  setSelectedPreset: (v: string) => void;
  type: string;
  setType: (v: any) => void;
  category: string;
  setCategory: (v: any) => void;
  maxUses: string;
  setMaxUses: (v: string) => void;
  maxUsesPerUser: string;
  setMaxUsesPerUser: (v: string) => void;
  dailyLimit: string;
  setDailyLimit: (v: string) => void;
  startsAt: string;
  setStartsAt: (v: string) => void;
  expiresAt: string;
  setExpiresAt: (v: string) => void;
  campaignName: string;
  setCampaignName: (v: string) => void;
  // Restrictions
  restrictionType: string;
  setRestrictionType: (v: any) => void;
  newUserDays: string;
  setNewUserDays: (v: string) => void;
  inactiveDays: string;
  setInactiveDays: (v: string) => void;
  minProjects: string;
  setMinProjects: (v: string) => void;
  maxProjects: string;
  setMaxProjects: (v: string) => void;
  emailVerified: boolean;
  setEmailVerified: (v: boolean) => void;
  neverPurchased: boolean;
  setNeverPurchased: (v: boolean) => void;
  // Recurring
  isRecurring: boolean;
  setIsRecurring: (v: boolean) => void;
  recurringInterval: string;
  setRecurringInterval: (v: any) => void;
  recurringCount: string;
  setRecurringCount: (v: string) => void;
}

const PRESET_LABELS: Record<string, string> = {
  welcome_new_users: '🎁 Welcome New Users',
  flash_sale: '🔥 Flash Sale',
  win_back_inactive: '🔄 Win Back Inactive',
  email_verification: '✉️ Email Verification',
  first_time_buyer: '🚀 First-Time Buyer',
  loyalty_reward: '👑 Loyalty Reward',
  influencer_code: '📢 Influencer Campaign',
};

export function PromoCodeForm(props: PromoCodeFormProps) {
  const presetKeys = Object.keys(props.presets || {});

  const handlePresetChange = (preset: string) => {
    props.setSelectedPreset(preset);
    props.onPresetChange(preset);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-emerald-600" />
          <h3 className="text-lg font-semibold text-slate-900">Create Promo Code</h3>
        </div>
        {props.selectedPreset && (
          <span className="px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full">
            {PRESET_LABELS[props.selectedPreset] || 'Custom Template'}
          </span>
        )}
      </div>

      <form onSubmit={props.onSubmit} className="space-y-6">
        {/* Quick Start - Preset Selector */}
        <div className="pb-6 border-b border-slate-200">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            <Zap className="w-4 h-4 inline mr-1" />
            Quick Start (Optional)
          </label>
          <div className="relative">
            <select
              value={props.selectedPreset}
              onChange={(e) => handlePresetChange(e.target.value)}
              className="w-full px-4 py-2.5 pr-10 border border-slate-300 rounded-lg bg-white appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
            >
              <option value="">Custom (Start from scratch)</option>
              {presetKeys.map((key) => (
                <option key={key} value={key}>
                  {PRESET_LABELS[key] || key}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
          </div>
          <p className="mt-1.5 text-xs text-slate-500">
            Choose a template to auto-fill settings, or start from scratch
          </p>
        </div>

        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              <Tag className="w-4 h-4 inline mr-1" />
              Code *
            </label>
            <input
              type="text"
              value={props.code}
              onChange={(e) => props.setCode(e.target.value.toUpperCase())}
              placeholder="BLACKFRIDAY2026"
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              <DollarSign className="w-4 h-4 inline mr-1" />
              Credits *
            </label>
            <input
              type="number"
              value={props.credits}
              onChange={(e) => props.setCredits(e.target.value)}
              placeholder="50"
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
              required
              min="1"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Description
          </label>
          <input
            type="text"
            value={props.description}
            onChange={(e) => props.setDescription(e.target.value)}
            placeholder="Black Friday special - 50 bonus credits"
            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
          />
        </div>

        {/* Advanced Options Toggle */}
        <button
          type="button"
          onClick={() => props.setShowAdvanced(!props.showAdvanced)}
          className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <span className="text-sm font-medium text-slate-700">
            <Settings className="w-4 h-4 inline mr-2" />
            Advanced Options
          </span>
          <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${props.showAdvanced ? 'rotate-180' : ''}`} />
        </button>

        {/* Advanced Options Content */}
        {props.showAdvanced && (
          <div className="space-y-6 pt-4 border-t border-slate-200">
            {/* Type & Category */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Type
                </label>
                <select
                  value={props.type}
                  onChange={(e) => props.setType(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
                >
                  <option value="public">Public</option>
                  <option value="targeted">Targeted</option>
                  <option value="unique">Unique</option>
                  <option value="recurring">Recurring</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Category
                </label>
                <select
                  value={props.category}
                  onChange={(e) => props.setCategory(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
                >
                  <option value="marketing">Marketing</option>
                  <option value="retention">Retention</option>
                  <option value="acquisition">Acquisition</option>
                  <option value="loyalty">Loyalty</option>
                </select>
              </div>
            </div>

            {/* Time Restrictions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Starts At
                </label>
                <input
                  type="datetime-local"
                  value={props.startsAt}
                  onChange={(e) => props.setStartsAt(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Expires At
                </label>
                <input
                  type="datetime-local"
                  value={props.expiresAt}
                  onChange={(e) => props.setExpiresAt(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
                />
              </div>
            </div>

            {/* Usage Limits */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Max Total Uses
                </label>
                <input
                  type="number"
                  value={props.maxUses}
                  onChange={(e) => props.setMaxUses(e.target.value)}
                  placeholder="500 (empty = ∞)"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Max Per User
                </label>
                <input
                  type="number"
                  value={props.maxUsesPerUser}
                  onChange={(e) => props.setMaxUsesPerUser(e.target.value)}
                  placeholder="1 (empty = ∞)"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Daily Limit
                </label>
                <input
                  type="number"
                  value={props.dailyLimit}
                  onChange={(e) => props.setDailyLimit(e.target.value)}
                  placeholder="50 (empty = ∞)"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
                  min="1"
                />
              </div>
            </div>

            {/* Campaign Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <Target className="w-4 h-4 inline mr-1" />
                Campaign Name
              </label>
              <input
                type="text"
                value={props.campaignName}
                onChange={(e) => props.setCampaignName(e.target.value)}
                placeholder="Black Friday 2026"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
              />
            </div>

            {/* User Restrictions */}
            <div className="pt-4 border-t border-slate-200">
              <label className="block text-sm font-medium text-slate-700 mb-3">
                <Users className="w-4 h-4 inline mr-1" />
                User Restrictions
              </label>
              <div className="space-y-4">
                <select
                  value={props.restrictionType}
                  onChange={(e) => props.setRestrictionType(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
                >
                  <option value="all">All Users</option>
                  <option value="new_users">New Users Only</option>
                  <option value="inactive_users">Inactive Users Only</option>
                  <option value="verified_only">Email Verified Only</option>
                  <option value="custom">Custom Rules</option>
                </select>

                {/* New Users Settings */}
                {(props.restrictionType === 'new_users' || props.restrictionType === 'custom') && (
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">
                      New User Definition (days since registration)
                    </label>
                    <input
                      type="number"
                      value={props.newUserDays}
                      onChange={(e) => props.setNewUserDays(e.target.value)}
                      placeholder="7"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
                      min="1"
                    />
                  </div>
                )}

                {/* Inactive Users Settings */}
                {(props.restrictionType === 'inactive_users' || props.restrictionType === 'custom') && (
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">
                      Inactive Definition (days since last login)
                    </label>
                    <input
                      type="number"
                      value={props.inactiveDays}
                      onChange={(e) => props.setInactiveDays(e.target.value)}
                      placeholder="60"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
                      min="1"
                    />
                  </div>
                )}

                {/* Custom Rules */}
                {props.restrictionType === 'custom' && (
                  <div className="space-y-3 pl-4 border-l-2 border-emerald-200">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1.5">
                          Min Projects
                        </label>
                        <input
                          type="number"
                          value={props.minProjects}
                          onChange={(e) => props.setMinProjects(e.target.value)}
                          placeholder="0"
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 text-sm"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1.5">
                          Max Projects
                        </label>
                        <input
                          type="number"
                          value={props.maxProjects}
                          onChange={(e) => props.setMaxProjects(e.target.value)}
                          placeholder="∞"
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 text-sm"
                          min="0"
                        />
                      </div>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={props.emailVerified}
                        onChange={(e) => props.setEmailVerified(e.target.checked)}
                        className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                      />
                      <span className="text-sm text-slate-700">Email verified required</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={props.neverPurchased}
                        onChange={(e) => props.setNeverPurchased(e.target.checked)}
                        className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                      />
                      <span className="text-sm text-slate-700">Never purchased before</span>
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Recurring Settings */}
            <div className="pt-4 border-t border-slate-200">
              <label className="flex items-center gap-2 cursor-pointer mb-3">
                <input
                  type="checkbox"
                  checked={props.isRecurring}
                  onChange={(e) => props.setIsRecurring(e.target.checked)}
                  className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                />
                <span className="text-sm font-medium text-slate-700">Recurring Code</span>
              </label>
              {props.isRecurring && (
                <div className="grid grid-cols-2 gap-4 pl-6">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">
                      Interval
                    </label>
                    <select
                      value={props.recurringInterval}
                      onChange={(e) => props.setRecurringInterval(e.target.value)}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 text-sm"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">
                      Max Uses
                    </label>
                    <input
                      type="number"
                      value={props.recurringCount}
                      onChange={(e) => props.setRecurringCount(e.target.value)}
                      placeholder="12"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 text-sm"
                      min="1"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
        >
          <Plus className="w-5 h-5" />
          Create Promo Code
        </button>
      </form>
    </div>
  );
}
